import { take } from "lodash";
import locale from "@bonliva-crm/locale";
import { Color } from "@bonliva-crm/ui";
import { Attachments } from "../attachments/attachments";
import { ContactPersonEmailSearch } from "../email-editor/contact-person-email-search";
import {
  Box,
  Divider,
  FormControl,
  InputBase,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Editor } from "@tinymce/tinymce-react4";
import { Controller, UseFormReturn } from "react-hook-form";
import { EmailEditorFormValues } from "./form-values";

type Props = {
  form: UseFormReturn<EmailEditorFormValues, any, EmailEditorFormValues>;
  editorInitialValue?: string;
  disabledFields?: Partial<Record<keyof EmailEditorFormValues, boolean>>;
  isCustomerSearch?: boolean;
};

export const EmailEditor = ({
  form: { control },
  editorInitialValue,
  disabledFields,
  isCustomerSearch
}: Props) => {
  const classes = useStyles();

  return (
    <FormControl component="fieldset" className={classes.root}>
      <section>
        <Controller
          render={({ field }) => {
            const hasMore = field.value.length > 50;
            const moreItems = hasMore ? field.value.length - 50 : 0;

            return (
              <Box className={classes.textContainer}>
                <Typography
                  variant="body1"
                  style={{ color: Color.DetailsInput }}
                >{`${locale.To}:`}</Typography>

                <Box flex="1">
                  <ContactPersonEmailSearch
                    {...field}
                    value={take(field.value, 50)}
                    creatable={true}
                    style={{ width: "100%" }}
                    disabled={disabledFields?.recipients}
                  />

                  {hasMore && (
                    <Box ml={2}>
                      <Typography variant="body2">+{moreItems}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          }}
          name="recipients"
          control={control}
        />
      </section>
      <Divider />
      <section>
        <Controller
          render={({ field }) => (
            <Box className={classes.textContainer}>
              <Typography
                variant="body1"
                style={{ color: Color.DetailsInput }}
              >{`${locale.Subject}:`}</Typography>
              <InputBase {...field} fullWidth={true} style={{ border: "none" }} />
            </Box>
          )}
          name="subject"
          control={control}
        />
      </section>
      <Divider />
      <section style={{ marginTop: "1rem" }}>
        <Controller
          render={({ field: { onChange, ...rest } }) => (
            <Editor
              {...rest}
              onInit={(_, editor) =>
                onChange(editor.getContent({ format: "text" }).trim())
              }
              onEditorChange={(newValue) => onChange(newValue.trim())}
              initialValue={editorInitialValue}
              init={{
                branding: false,
                height: "30vh",
                menubar: false,
                plugins: [
                  "autolink",
                  "lists",
                  "link",
                  "image",
                  "searchreplace",
                  "code",
                  "media"
                ],
                toolbar:
                  "bold italic underline strikethrough | " +
                  "link | " +
                  "numlist bullist |" +
                  "indent |" +
                  "code",
                content_style: "body { font-family:Arial; font-size:14px }"
              }}
            />
          )}
          name="body"
          control={control}
        />
      </section>
      <section>
        <Controller
          render={({ field }) => <Attachments {...field} />}
          name="attachments"
          control={control}
        />
      </section>
    </FormControl>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "0.5rem"
  },
  textContainer: {
    display: "flex",
    flexDirection: "row",
    columnGap: "0.5rem",
    alignItems: "center"
  }
}));
